import { useMemo } from 'react';

import { FolderId } from 'modules/domains/foldering';

import { GRAPHQL_CONTENT_FILTER, WORKFLOW_STATUS_FILTER } from '../constants';

export const getJourneyVisitorFromOptions = (options: string[]) => {
  if (options.length === 0 || options.length > 1) {
    return null;
  }
  const visitorTracking = options[0];
  return Boolean(visitorTracking.toLocaleLowerCase() === 'true');
};
/**
 * `useQuery` re-fetches from /graphql whenever the variables object changes. Therefore,
 * this hook wraps variables in a useMemo to ensure that it maintains its identity
 * across render cycles, preventing unnecessary re-fetches.
 */
export const useWorkflowsQueryVariables = (options: {
  currentPageSize: number;
  currentPage: number;
  statusFilterIds: string[];
  workflowTypeFilterIds: string[];
  triggerTypeFilterIds: string[];
  creatorFilterIds: string[];
  modifierFilterIds: string[];
  journeyVisitorTrackingIds: string[];
  search: string;
  sortedBy: string;
  sortDirection: string;
  folderId: FolderId;
  labelFilterIds: string[];
}) => {
  const content =
    options.statusFilterIds.length === 1 &&
    options.statusFilterIds[0] === WORKFLOW_STATUS_FILTER.ARCHIVED
      ? [GRAPHQL_CONTENT_FILTER.CONTENT_ITEM]
      : null;

  const workflowsQueryVariables = useMemo(
    () => ({
      filterInfo: {
        workflowType:
          options.workflowTypeFilterIds.length > 0 ? options.workflowTypeFilterIds : null,
        workflowState: options.statusFilterIds.length > 0 ? options.statusFilterIds : null,
        trigger: options.triggerTypeFilterIds.length ? options.triggerTypeFilterIds : null,
        createdByUserId: options.creatorFilterIds.length ? options.creatorFilterIds : null,
        updatedByUserId: options.modifierFilterIds.length ? options.modifierFilterIds : null,
        labelIds: options.labelFilterIds.length ? options.labelFilterIds : null,
        visitorTracking: getJourneyVisitorFromOptions(options.journeyVisitorTrackingIds),
      },
      pagination: {
        limit: options.currentPageSize,
        offset: (options.currentPage - 1) * options.currentPageSize,
      },
      search: options.search,
      sort: {
        sortBy: options.sortedBy,
        sortDirection: options.sortDirection,
      },
      folderId: options.folderId,
      recursive: !!options.search.length,
      content,
    }),
    [
      options.currentPageSize,
      options.currentPage,
      options.search,
      options.statusFilterIds,
      options.workflowTypeFilterIds,
      options.triggerTypeFilterIds,
      options.sortedBy,
      options.sortDirection,
      options.creatorFilterIds,
      options.modifierFilterIds,
      options.folderId,
      options.labelFilterIds,
      options.journeyVisitorTrackingIds,
    ],
  );

  return workflowsQueryVariables;
};
